/**
 * 이상한 방 - 싱글플레이 10단계 튜토리얼 정의
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 5.3절(허용 동사는 설정값) · 5.4절(동사의 단계적 도입) ·
 *           9.5절(완전 싱글의 포지션 = 튜토리얼) · `이상한방-컨셉시트-v0.1.md` 4.2절(제거가 최고 난도)
 *
 * ## 목적은 "도전"이 아니라 "졸업"이다
 *
 * 9.5절이 완전 싱글의 포지션을 **튜토리얼**로 못박았다. 메인은 멀티이고 싱글은 그 준비 과정이다.
 * 따라서 성공 조건은 "어려웠다"가 아니라 **"멀티 규칙을 다 익힌 상태로 내보냈다"**이며,
 * 여기서 막히는 플레이어는 멀티에 도달조차 못 한다. 그래서 이 표의 숫자는 전부 관대한 쪽으로 기운다.
 *
 * ## 개편 이전에 무엇이 틀렸는가 (실측)
 *
 * 개편 전 싱글은 9구획에 오브젝트 **379~391개** · 변화 12개였다 - 변화 비율 약 3%.
 * 통상적인 틀린그림찾기는 요소 30~50개에 틀린 곳 5~7개(10~20%)다. 자릿수가 어긋나 있었고,
 * 개발자의 플레이 평가는 "엄청 어렵다"였다. 사실상 **튜토리얼 없이 10단계부터 시작한 것**이다.
 * 이 표는 셋을 고친다: **개수**(30 → 180, 개편 전의 절반 이하) · **공간**(방 하나 → 아파트) ·
 * **동사**(5.4절의 단계적 도입 - 한 단계에 하나씩만 새로 배운다).
 *
 * ## 개수를 한 번 더 덜었다 - 15 → 110 (2026-09-28, 플레이테스트 "여전히 너무 어렵다")
 *
 * 혼자 하기의 자리는 튜토리얼이다(상태모델 9.5절). 찾기가 어려운 이유의 대부분은 **안 바뀐 물건의 수**다 -
 * 이상한 곳 하나를 가리는 잡음이 곧 방의 물건이다. 그래서 **잡음만** 덜었다: 변화 수·외우기·수색 시간·
 * 탄환·클리어 기준·동사 도입 순서는 한 글자도 안 바뀌었다(한 번에 손잡이 하나 - 다음 평가가 무엇의
 * 결과인지 알 수 있어야 한다).
 *
 * 앞을 더 많이 덜었다(1단계 30 → 15, 10단계 180 → 110). 규칙을 배우는 자리에서 잡음은 학습을
 * 가리기만 하고, 뒤는 졸업 시험이라 덜 덜어낸다. 변화 비율은 1단계 20% · 10단계 11%로 통상적인
 * 틀린그림찾기의 범위(10~20%) 안에 들어왔다. 전 단계에서 변화 생성이 모자라지 않는 것은 시도 40회로
 * 확인했다(가장 빡빡한 1단계도 물건 15개에 변화 3개).
 *
 * **매일의 방은 덜지 않았다**(생성기 기본값 180) - 졸업한 사람끼리 같은 방을 견주는 자리라 튜토리얼이 아니다.
 *
 * ## 탄환 곡선이 다시 짜였다 - 이번엔 방향이 반대다 (탄창 개편)
 *
 * 보급의 자리가 세 번 옮겨졌다. 정답 방 리필 → 방 바닥의 월드 아이템 → **계정 단위 탄창**
 * (`game/magazines.ts`). 마지막 이동이 이 표에 남긴 결과는 하나다:
 * **라운드 안에서 확실히 손에 쥐는 탄환은 `startAmmo` 하나뿐이다.**
 *
 * 그래서 이 표가 지켜야 하는 것이 명문화된다.
 *
 * > **탄창을 한 개도 쓰지 않고 모든 단계를 클리어할 수 있어야 한다.**
 *
 * 탄창이 없으면 진행이 막히는 순간 그건 페이월이고, 튜토리얼에서 막히면 멀티까지 도달을 못 한다.
 * 그래야 나중에 광고·결제가 강요가 아니라 선택이 된다. 이 불변식은 주석이 아니라
 * `minAmmoWithoutMagazines()`가 계산하고 `validateLevels`가 검사하며 테스트가 고정한다.
 *
 * ### 왜 탄환이 뒤로 갈수록 늘어나는가 (곡선의 방향이 뒤집혔다)
 *
 * 직전 표는 전 단계 12발 고정이었고, 그 위에 보급품 0~6개(최대 +18발)가 얹혀 후반의 실질 예산을
 * 만들었다. 그 보급품이 통째로 사라졌으므로 12발을 그대로 두면 **후반 단계만 예산이 60% 깎인다.**
 * 개편이 "보급 방식 변경"이 아니라 "난이도 대폭 상승"이 되는 셈이고, 개발자가 두 번
 * "너무 어렵다"고 한 판을 더 어렵게 만드는 것은 이번 작업의 목적과 정반대다.
 *
 * 그래서 절대량은 **오른다**: 12 → 16. 대신 **관대함은 여전히 내려간다.**
 * 곡선을 재는 자가 바뀐 것이지 곡선이 뒤집힌 것이 아니다.
 *
 * ```
 *   관대함 = 시작 탄환 / 클리어에 필요한 발견 수
 *   1단계 12/2 = 6.0  …  5단계 12/4 = 3.0  …  10단계 16/7 = 2.29   (단조 감소)
 * ```
 *
 * 필요 발견 수(`minFound`)가 2에서 7로 오르는 동안 탄환은 12에서 16까지만 오르므로,
 * "정답 하나를 맞히기까지 몇 번 틀려도 되는가"는 계속 줄어든다. 그것이 난이도 곡선이다.
 * 12발이 앞의 일곱 단계에서 그대로인 것은 **바닥**이기 때문이다 - 초반 단계는 변화가
 * 3~4개뿐이라 애초에 탄환으로 지는 판이 아니고, 여기서 더 깎아 봐야 얻는 것이 없다.
 *
 * 대가를 하나 알고 간다: `startAmmo`는 탄환 상한이기도 해서(`gameState.ammoCapacity`)
 * 12를 넘는 8~10단계는 HUD의 탄피 표식 칸 수(12)를 넘긴다. HUD는 그 줄을 감추고 큰 숫자만
 * 남기도록 이미 만들어져 있으므로(`ui/gameHud.ts`의 `AMMO_PIP_MAX`) 거짓말은 하지 않는다.
 *
 * 라운드당 탄창 사용 상한과 개당 회복량(`maxMagazinesPerRound`·`ammoPerMagazine`)은 단계가
 * 건드리지 않는다. 손잡이를 더 늘리면 "6발짜리 2개"와 "4발짜리 3개"의 체감을 동시에
 * 이야기해야 하고, 그러면 플레이테스트가 읽히지 않는다.
 *
 * ## 쿨타임 5초가 만든 여유를 어디에 썼는가
 *
 * 정답 방 재입장 쿨타임이 30초에서 5초로 내려왔다(`tuning.ts`). 왕복 한 번의 대가가 사라졌으니
 * 전 단계가 그만큼 쉬워졌는데, 개발자의 평가는 여전히 "너무 어렵다"였다. 그래서 여유를 아끼지 않고
 * **더 쉬운 쪽으로** 썼다. 이번에 움직인 것은 셋이다:
 *
 *  - **수색 시간** 60~300초 → 90~300초. 늘린 것은 앞의 여덟 단계뿐이다. 9·10단계는 이미 멀티
 *    기본값(300초)이라 더 줄 수 없고, 애초에 초반에서 시간에 쫓기는 것은 배움이 아니라 조급함이다.
 *  - **클리어 기준** 최대 70%였던 것이 55~67%로 내려왔다. 상한(`MAX_CLEAR_RATIO`)은 그대로 두고
 *    **실제 값**만 내렸다 - 규칙을 느슨하게 푸는 것과 이 표가 관대해지는 것은 다른 일이다.
 *  - **보급품**이 3단계부터 상시로 깔렸다. 그 보급품은 이번 개편에서 탄창으로 대체됐고,
 *    같은 여유를 시작 탄환이 이어받는다(위 "탄환 곡선" 절).
 *
 * 오브젝트 수·변화 수·외우기 시간은 한 칸도 건드리지 않았다. 직전 개편이 밀도를 391에서 180으로
 * 이미 절반 이하로 내렸고, 한 번에 둘을 움직이면 다음 플레이테스트가 **무엇 때문에 쉬워졌는지**를
 * 말해 주지 못한다. 측정할 수 없는 개선은 개선이 아니다(컨셉시트 8절).
 *
 * ## 데이터만 두는 이유 · 시드
 *
 * `planSize`는 **요청**이지 평면도가 아니다. `FloorPlan` 해석은 평면도 레지스트리의 몫이며
 * 이 파일은 `core/floorPlan`도 Three.js도 import 하지 않는다 - 그래야 곡선이 렌더러 없이 고정된다.
 * 시드는 단계 번호에서 유도된다. 같은 1단계가 누구에게나 같은 방이어야 힌트가 힌트로 기능하고
 * "여기서 막혔다"는 제보가 개발자 화면에서 재현되기 때문이며, 그래서 여기엔 `Math.random()`이 없다.
 */

import type { Verb } from '../core/types'
import type { Tuning } from './tuning'

/* --- 타입 ------------------------------------------------------------------- */

/** 맵 컨셉. 지금은 집 하나뿐. 컨셉이 늘면 소품 레시피 책이 통째로 갈리므로 레벨이 그 선택을 들고 있다. */
export type LevelConceptId = 'home'

/** 공간 규모. 실제 평면도로의 변환은 평면도 레지스트리가 한다(이 파일은 이름만 정한다). */
export type PlanSize = 'room' | 'two-rooms' | 'small-house' | 'house' | 'apartment'

/** 규모의 크기 순서. 곡선 검증과 UI 정렬이 **같은 배열 하나**를 본다(두 곳에 적으면 한쪽만 고치게 된다). */
export const PLAN_SIZE_ORDER: readonly PlanSize[] = ['room', 'two-rooms', 'small-house', 'house', 'apartment']

/** 규모의 서열. 값이 클수록 넓다. */
export function planSizeRank(size: PlanSize): number {
  return PLAN_SIZE_ORDER.indexOf(size)
}

/**
 * 단계 하나의 정의. `tuning`이 `Partial<Tuning>`인 것이 이 타입의 핵심이다 - 단계가 정하는 것은
 * 곡선에 관여하는 몇 개뿐(변화 수·시간·탄환·동사)이고, 정답 방 쿨타임처럼 **멀티와 공유되는 규칙은
 * 기본 튜닝 그대로 둔다.** 튜토리얼에서만 다르게 굴러가는 규칙을 배우면 졸업이 성립하지 않는다.
 */
export type LevelDef = Readonly<{
  /** 1부터 시작하는 연속 번호. 해금 순서가 곧 이 번호다. */
  id: number
  /** 한국어 표시 이름. */
  name: string
  conceptId: LevelConceptId
  planSize: PlanSize
  /** 목표 오브젝트 수. 생성기에는 **상한**으로 들어간다(자리가 없으면 이보다 적게 나온다). */
  objectTarget: number
  /** 단계별 고정 시드. 같은 단계는 누구에게나 같은 방이다. */
  seed: string
  tuning: Partial<Tuning>
  /** 클리어 조건. 튜토리얼이므로 **전부 찾을 필요는 없다**(아래 MAX_CLEAR_RATIO 참조). */
  clearCondition: Readonly<{ minFound: number }>
  /** 이 단계가 가르치는 것 한 줄. 시작 화면에 그대로 띄운다. */
  hint?: string
}>

/** 화면이 읽는 평평한 단계 정보(`levelSummary`의 결과). 중첩 경로 없이 이름만으로 그릴 수 있다. */
export type LevelSummary = Readonly<{
  id: number
  name: string
  hint: string
  changeCount: number
  huntSeconds: number
  startAmmo: number
  allowedVerbs: readonly Verb[]
  objectCount: number
}>

/* --- 곡선 상수 --------------------------------------------------------------- */

/** 첫 단계 번호. 항상 해금돼 있는 유일한 단계다. */
export const FIRST_LEVEL_ID = 1

/**
 * 클리어에 필요한 발견 비율의 상한(70%). 100%를 요구하면 마지막 한 개
 * (대개 제거 - 컨셉시트 4.2절의 최고 난도)에서 전원이 막힌다. 튜토리얼에서 막히면 멀티에 못 가므로
 * **놓친 것이 있어도 다음 단계로 보낸다.** 놓친 변화는 리빌에서 보여 주므로 배움은 그대로 남는다.
 */
export const MAX_CLEAR_RATIO = 0.7

/**
 * 클리어에 필요한 정답 **하나당** 허용하는 오답 횟수.
 *
 * 이 상수가 "탄창 없이 깰 수 있다"를 수치로 옮긴 것이다. 정답 사격은 탄환을 먹지 않고
 * (`hunt.ammoCostForOutcome`) 오답만 1발씩 먹으므로, 라운드에서 버틸 수 있는 오답 횟수는
 * 오직 시작 탄환이 정한다. 2로 잡은 근거는 조작이다 - 이 게임의 사격은 화면 탭이고
 * **빈 곳을 누르는 것도 오답 1발**이라(`hunt.resolveShot`이 `null`을 miss로 판정한다),
 * 조준 실수와 오판을 합치면 정답 하나에 두 번쯤은 헛발질한다고 봐야 한다.
 *
 * 1이면 "한 번 틀리면 한 번 더 노릴 수 없다"가 되어 관찰 게임이 아니라 사격 게임이 되고,
 * 3 이상이면 후반 단계에서도 탄환이 사실상 무한이라 잔탄이라는 자원이 의미를 잃는다.
 */
export const MISSES_PER_REQUIRED_FIND = 2

/**
 * 이 단계를 **탄창 없이** 깨는 데 필요한 최소 시작 탄환.
 *
 * `+1`이 붙는 이유는 종료 조건이다 - 잔탄이 0이 되는 순간 라운드가 끝나므로(`gameState.onMiss`),
 * 탄환 A발로 마음 놓고 틀릴 수 있는 횟수는 A가 아니라 **A-1**이다. 마지막 한 발은 쏠 수는 있지만
 * 그것이 빗나가면 그 자리에서 판이 끝나므로 예산으로 세지 않는다.
 */
export function minAmmoWithoutMagazines(level: LevelDef): number {
  return level.clearCondition.minFound * MISSES_PER_REQUIRED_FIND + 1
}

/**
 * 이 단계의 관대함. 값이 클수록 여유가 많다.
 *
 * 절대 탄환량이 아니라 **이 비율**이 난이도 곡선이다(파일 상단). 뒤로 갈수록 필요 발견 수가
 * 탄환보다 빠르게 오르므로 이 값은 단조로 줄어들고, 그 감소가 곧 "점점 어려워진다"의 정의다.
 * 곡선 검증(`CURVE_AXES`)과 화면·테스트가 **이 함수 하나**를 본다 - 같은 개념을 두 곳에 적으면
 * 한쪽만 고치는 날이 오고, 그날 곡선은 조용히 어긋난다.
 *
 * `base`는 단계가 탄환을 생략했을 때만 쓰인다. 지금 표는 전 단계가 값을 갖고 있어
 * (`validateLevels`가 그것을 요구한다) 실제로는 쓰이지 않지만, 검증기가 임의의 표를 받는 이상
 * 생략된 값에 대한 답이 있어야 한다.
 */
export function ammoGenerosity(level: LevelDef, base?: Tuning): number {
  const startAmmo = level.tuning.startAmmo ?? base?.startAmmo ?? 0
  return startAmmo / level.clearCondition.minFound
}

/**
 * 단계 시드의 접두사·자릿수. 시드는 `level-01` 꼴로 **번호에서 유도**된다.
 * `-r<숫자>` 꼴이 아니므로 "한 판 더"의 라운드 시드 파싱(`session.parseRoundSeed`)과 섞이지 않는다.
 */
const LEVEL_SEED_PREFIX = 'level-'
const LEVEL_SEED_DIGITS = 2

/* --- 동사의 단계적 도입 (상태모델 5.4절) --------------------------------------- */

/**
 * 1단계 - 제거만. 먼저 두는 이유는 난도가 아니라 **판정 경로**다. 없는 물건은 클릭할 수 없어
 * 빈 셀을 조준해야 하는데(6.1절) 그게 가장 낯선 조작이고, 가장 낯선 것을 가장 작은 방에서 배운다.
 * 이후 상수는 앞 단계를 그대로 펼쳐 담는다 - 동사는 **더해지기만** 한다는 사실을 구조가 강제한다.
 */
const VERBS_REMOVE_ONLY: readonly Verb[] = ['remove']
/** 2단계 - 추가. "빈 자리"와 "못 보던 것" 양쪽을 봐야 한다. */
const VERBS_WITH_ADD: readonly Verb[] = [...VERBS_REMOVE_ONLY, 'add']
/** 3단계 - 이동. 개수 세기 전략이 처음으로 무너진다(5.4절 주의사항). */
const VERBS_WITH_MOVE: readonly Verb[] = [...VERBS_WITH_ADD, 'move']
/** 5단계 - 회전. 형태가 아니라 **방향**을 기억해야 한다. */
const VERBS_WITH_ROTATE: readonly Verb[] = [...VERBS_WITH_MOVE, 'rotate']
/** 7단계 - 색까지. 멀티 기본값과 같은 다섯 동사가 된다. */
const VERBS_ALL: readonly Verb[] = [...VERBS_WITH_ROTATE, 'recolor']

/* --- 10단계 ------------------------------------------------------------------ */

/** 표 한 줄. `LevelDef`를 손으로 적으면 단계마다 20줄이 되고, **표로 보이지 않으면 곡선의 어긋남을 눈으로 못 잡는다.** */
type LevelRow = Readonly<{
  id: number
  name: string
  planSize: PlanSize
  /** 목표 오브젝트 수 → `objectTarget`. */
  objects: number
  /** 초 단위 두 값과 개수 두 값은 각각 `changeCount`·`watchSeconds`·`huntSeconds`·`startAmmo`가 된다. */
  changes: number
  watch: number
  hunt: number
  /**
   * 시작 탄환 → `startAmmo`. **라운드 안에서 손에 쥐는 탄환의 전부다**(탄창은 계정 자원이라
   * 없을 수도 있다). 그래서 이 값은 `minAmmoWithoutMagazines(level)` 이상이어야 하고,
   * `validateLevels`가 그것을 검사한다 - 표를 손볼 때 이 불변식이 조용히 깨지는 것을 막는다.
   */
  ammo: number
  verbs: readonly Verb[]
  minFound: number
  hint: string
}>

/**
 * 한 줄을 단계로 편다. 시드는 여기서 **번호로부터 유도**된다(`level-07`) -
 * 손으로 적으면 복사-붙여넣기 한 번에 두 단계가 같은 방이 되고, 그건 데이터만 봐서는 안 보인다.
 */
function defineLevel(row: LevelRow): LevelDef {
  return {
    id: row.id,
    name: row.name,
    conceptId: 'home',
    planSize: row.planSize,
    objectTarget: row.objects,
    seed: `${LEVEL_SEED_PREFIX}${String(row.id).padStart(LEVEL_SEED_DIGITS, '0')}`,
    tuning: {
      changeCount: row.changes, watchSeconds: row.watch, huntSeconds: row.hunt,
      startAmmo: row.ammo, allowedVerbs: row.verbs,
    },
    clearCondition: { minFound: row.minFound },
    hint: row.hint,
  }
}

/**
 * 튜토리얼 곡선. 아래 열 줄이 곧 표다(그래서 주석에 표를 또 그리지 않는다).
 *
 * **탄환은 12발에서 시작해 16발로 끝난다.** 늘어나는데도 점점 어려워지는 이유는
 * 필요 발견 수가 더 빨리 늘기 때문이며, 그 계산은 파일 상단의 "탄환 곡선" 절에 있다.
 *
 *  - **12발은 바닥이다.** 1단계에서 탄환이 떨어져 실패하는 것이 이 게임이 만들 수 있는 최악의
 *    첫인상이고(규칙을 배우기도 전에 벌부터 받는 셈이다), 초반 단계는 변화가 3~4개뿐이라
 *    여기서 더 깎아도 난이도가 생기지 않고 사고만 생긴다.
 *  - **8단계부터 오른다.** 그 지점이 `minAmmoWithoutMagazines`가 12를 넘어서는 곳,
 *    즉 클리어 기준이 6개가 되는 곳이다. 값은 규칙에서 나오지 취향에서 나오지 않는다.
 *  - **10단계는 멀티 기본값(16발)과 한 글자도 다르지 않다.** 졸업 시점에 규칙이 하나로 합쳐지지
 *    않으면 마지막 단계가 튜토리얼이 아니라 난이도 절벽의 앞면이 된다.
 *
 * 외우기 시간은 함께 자란다 - 30개짜리 방을 60초 보는 것은 지루함이지 학습이 아니다.
 *
 * 문구(`name`·`hint`)는 사전이 이긴다(`ui/levelSelect`가 `level.<id>.name` 키를 먼저 본다).
 * 여기 적힌 한국어는 키가 빠졌을 때만 쓰이는 **대비값**이라 사전과 같은 뜻으로 유지한다.
 */
export const LEVELS: readonly LevelDef[] = [
  defineLevel({
    id: 1, name: '첫 번째 방', planSize: 'room', objects: 15,
    changes: 3, watch: 30, hunt: 90, ammo: 12, verbs: VERBS_REMOVE_ONLY, minFound: 2,
    hint: '물건 하나가 없어진다. 비어 버린 자리를 쏴라.',
  }),
  defineLevel({
    id: 2, name: '하나 더 늘었다', planSize: 'room', objects: 20,
    changes: 4, watch: 30, hunt: 90, ammo: 12, verbs: VERBS_WITH_ADD, minFound: 2,
    hint: '없던 물건도 생긴다. 빈 자리와 낯선 물건을 같이 봐라.',
  }),
  defineLevel({
    id: 3, name: '자리를 옮긴 것', planSize: 'two-rooms', objects: 28,
    changes: 5, watch: 40, hunt: 150, ammo: 12, verbs: VERBS_WITH_MOVE, minFound: 3,
    hint: '물건이 자리를 옮긴다. 개수만 세면 놓친다. 벽과의 간격을 봐라.',
  }),
  defineLevel({
    id: 4, name: '두 방 사이', planSize: 'two-rooms', objects: 35,
    changes: 6, watch: 40, hunt: 150, ammo: 12, verbs: VERBS_WITH_MOVE, minFound: 3,
    hint: '한 방을 다 본 뒤에 옆방으로 가라. 왔다 갔다 하면 둘 다 못 외운다.',
  }),
  defineLevel({
    id: 5, name: '돌아앉은 것', planSize: 'small-house', objects: 45,
    changes: 7, watch: 45, hunt: 210, ammo: 12, verbs: VERBS_WITH_ROTATE, minFound: 4,
    hint: '물건이 방향을 튼다. 의자 앞면이 어디를 보고 있었나.',
  }),
  defineLevel({
    id: 6, name: '작은 집', planSize: 'small-house', objects: 55,
    changes: 8, watch: 45, hunt: 210, ammo: 12, verbs: VERBS_WITH_ROTATE, minFound: 5,
    hint: '가구는 무리로 놓여 있다. 낱개 말고 무리째 외워라.',
  }),
  defineLevel({
    id: 7, name: '색이 바뀐 자리', planSize: 'house', objects: 65,
    changes: 9, watch: 50, hunt: 270, ammo: 12, verbs: VERBS_ALL, minFound: 5,
    hint: '색이 바뀐다. 같은 물건이 여럿이면 서로 비교해 봐라.',
  }),
  defineLevel({
    id: 8, name: '원래 방을 보러 가기', planSize: 'house', objects: 80,
    changes: 10, watch: 50, hunt: 270, ammo: 14, verbs: VERBS_ALL, minFound: 6,
    hint: '원래 방에 다녀올 수 있다. 대신 나오면 5초를 기다린다.',
  }),
  defineLevel({
    id: 9, name: '아파트', planSize: 'apartment', objects: 95,
    changes: 11, watch: 60, hunt: 300, ammo: 14, verbs: VERBS_ALL, minFound: 6,
    hint: '아홉 칸이다. 남은 시간과 탄환을 보면서 순서를 정해라.',
  }),
  defineLevel({
    id: 10, name: '이상한 방', planSize: 'apartment', objects: 110,
    changes: 12, watch: 60, hunt: 300, ammo: 16, verbs: VERBS_ALL, minFound: 7,
    hint: '여럿이 할 때와 똑같은 규칙이다. 여기까지 왔으면 준비는 끝났다.',
  }),
]

/** 단계 수. UI가 "3 / 10"을 그릴 때 쓰는 값이며 `LEVELS`와 갈릴 수 없다. */
export const LEVEL_COUNT = LEVELS.length

/** 마지막 단계 번호. 이걸 깨면 멀티로 보낸다. */
export const LAST_LEVEL_ID = FIRST_LEVEL_ID + LEVEL_COUNT - 1

/* --- 조회 ------------------------------------------------------------------- */

export function getLevel(id: number): LevelDef | undefined {
  return LEVELS.find((level) => level.id === id)
}

/**
 * 기본 튜닝 위에 단계 값을 얹는다. **덮어쓰기는 단계가 이긴다.**
 *
 * `base`를 인자로 받는 이유는 URL 덮어쓰기(`resolveTuning`)와의 순서 때문이다 -
 * 개발자가 `?changes=1`로 단계를 검사할 수 있어야 하는지, 단계 값이 항상 이겨야 하는지는
 * 호출부가 정할 문제이고, 이 함수는 "얹는다"는 사실만 책임진다.
 */
export function levelTuning(level: LevelDef, base: Tuning): Tuning {
  return { ...base, ...level.tuning }
}

/**
 * 이 단계를 깼는가. **판정을 여기 하나로 모은다** -
 * 진행 저장(`progress.ts`)과 결과 화면이 각자 비교식을 들면 둘이 갈라지고,
 * 갈라진 순간 "클리어했다고 떴는데 다음 단계가 안 열린다"가 된다.
 */
export function meetsClearCondition(level: LevelDef, foundCount: number): boolean {
  return foundCount >= level.clearCondition.minFound
}

/* --- 화면용 요약 -------------------------------------------------------------- */

/**
 * 단계 하나를 **화면이 읽는 평평한 모양**으로 편다. UI가 `LevelDef`를 직접 읽으면
 * `tuning.changeCount` 같은 중첩 경로가 화면 코드에 박혀 그때부터 튜닝 구조를 못 바꾼다.
 * `base`를 받는 이유는 단계가 생략한 값도 실제 값으로 채워 넘겨야 하기 때문이다.
 */
export function levelSummary(level: LevelDef, base: Tuning): LevelSummary {
  const tuning = levelTuning(level, base)
  return {
    id: level.id,
    name: level.name,
    hint: level.hint ?? '',
    changeCount: tuning.changeCount,
    huntSeconds: tuning.huntSeconds,
    startAmmo: tuning.startAmmo,
    allowedVerbs: tuning.allowedVerbs,
    objectCount: level.objectTarget,
  }
}

/* --- 검증 ------------------------------------------------------------------- */

/** 곡선이 단조인지 볼 때 쓰는 축. 한 단계를 끼워 넣어도 검사가 자동으로 따라온다. */
type CurveAxis = Readonly<{
  label: string
  of: (level: LevelDef) => number
  /** 'up' = 감소 금지, 'down' = 증가 금지. */
  direction: 'up' | 'down'
}>

const CURVE_AXES: readonly CurveAxis[] = [
  { label: '오브젝트 수', of: (level) => level.objectTarget, direction: 'up' },
  { label: '변화 수', of: (level) => level.tuning.changeCount ?? 0, direction: 'up' },
  { label: '수색 시간', of: (level) => level.tuning.huntSeconds ?? 0, direction: 'up' },
  { label: '외우기 시간', of: (level) => level.tuning.watchSeconds ?? 0, direction: 'up' },
  { label: '클리어 기준', of: (level) => level.clearCondition.minFound, direction: 'up' },
  { label: '공간 규모', of: (level) => planSizeRank(level.planSize), direction: 'up' },
  /*
   * 시작 탄환은 **오르기만 한다.** 필요 발견 수가 뒤로 갈수록 늘고, 탄환이 그것을 따라가지 못하면
   * "탄창 없이 클리어"라는 대전제가 깨지기 때문이다. 중간에 한 단계만 내리면 그 단계가
   * 곡선의 구멍이 되는데, 데이터만 봐서는 눈에 띄지 않는다.
   */
  { label: '시작 탄환', of: (level) => level.tuning.startAmmo ?? 0, direction: 'up' },
  /*
   * **난이도 곡선의 본체는 이 축이다.** 절대 탄환량이 아니라 "필요한 정답 하나당 몇 발이 있는가"가
   * 체감을 정한다(파일 상단). 탄환이 오르는데도 게임이 어려워지는 것이 이 축으로만 보이고,
   * 여기가 뒤집히면 중간 어딘가가 마지막 단계보다 관대해졌다는 뜻이다.
   *
   * 소수 나눗셈을 그대로 비교하는 것이 안전한 이유: 두 항이 모두 작은 정수라 비율이
   * 이진 부동소수에서 정확히 표현되거나(2의 거듭제곱 분모), 오차가 있어도 단계 간 간격
   * (최소 0.05)보다 열 자릿수 이상 작다.
   */
  { label: '탄환 여유', of: (level) => ammoGenerosity(level), direction: 'down' },
]

function validateCurve(levels: readonly LevelDef[], issues: string[]): void {
  for (const axis of CURVE_AXES) {
    for (let index = 1; index < levels.length; index += 1) {
      const previous = levels[index - 1]
      const current = levels[index]
      if (previous === undefined || current === undefined) continue

      const before = axis.of(previous)
      const after = axis.of(current)
      const regressed = axis.direction === 'up' ? after < before : after > before
      if (regressed) {
        issues.push(`${String(current.id)}단계의 ${axis.label}가 곡선을 거스른다: ${String(before)} → ${String(after)}`)
      }
    }
  }
}

function validateVerbs(levels: readonly LevelDef[], issues: string[]): void {
  for (let index = 0; index < levels.length; index += 1) {
    const current = levels[index]
    if (current === undefined) continue

    const verbs = current.tuning.allowedVerbs
    if (verbs === undefined || verbs.length === 0) {
      issues.push(`${String(current.id)}단계에 허용 동사가 없다 - 변화를 만들 수 없다`)
      continue
    }
    if (new Set(verbs).size !== verbs.length) issues.push(`${String(current.id)}단계의 허용 동사에 중복이 있다`)

    const previous = levels[index - 1]
    const before = previous?.tuning.allowedVerbs
    if (before === undefined) continue

    // 5.4절: 동사는 **더해지기만** 한다. 배운 것을 다음 단계에서 빼앗지 않는다.
    for (const verb of before) {
      if (!verbs.includes(verb)) {
        issues.push(`${String(current.id)}단계가 이전 단계의 동사 '${verb}'를 잃었다`)
      }
    }
  }
}

function validateLevel(level: LevelDef, index: number, issues: string[]): void {
  const label = `${String(level.id)}단계`

  if (level.id !== FIRST_LEVEL_ID + index) issues.push(`${label}의 번호가 순서와 맞지 않는다`)
  if (level.name.trim() === '') issues.push(`${label}에 이름이 없다`)
  if (!Number.isInteger(level.objectTarget) || level.objectTarget <= 0) issues.push(`${label}의 오브젝트 목표가 양의 정수가 아니다`)

  const changeCount = level.tuning.changeCount
  if (changeCount === undefined || !Number.isInteger(changeCount) || changeCount <= 0) {
    issues.push(`${label}의 변화 수가 양의 정수가 아니다`)
    return
  }
  if (changeCount > level.objectTarget) {
    issues.push(`${label}의 변화 수가 오브젝트 수보다 많다`)
  }

  const minFound = level.clearCondition.minFound
  if (!Number.isInteger(minFound) || minFound < 1) {
    issues.push(`${label}의 클리어 기준이 1 이상의 정수가 아니다`)
    return
  }
  // 전량 발견을 요구하면 튜토리얼이 벽이 된다. 상한을 데이터가 아니라 규칙으로 막는다.
  if (minFound > changeCount * MAX_CLEAR_RATIO) {
    issues.push(
      `${label}의 클리어 기준이 관대하지 않다: ${String(minFound)}/${String(changeCount)} > ${String(MAX_CLEAR_RATIO)}`,
    )
  }

  /*
   * **이 검사가 밸런스 대전제의 집행자다**(파일 상단: "탄창을 한 개도 쓰지 않고 클리어").
   *
   * 탄창은 계정 자원이라 없을 수 있고, 없을 때 진행이 막히면 그건 페이월이다. 여기서 걸리면
   * 답은 하나뿐이다 - **시작 탄환을 올린다.** 클리어 기준을 내려서 통과시키는 것은 같은 문제를
   * 다른 이름으로 덮는 것이고, 그 순간 이 검사는 아무것도 지키지 않게 된다.
   */
  const startAmmo = level.tuning.startAmmo
  if (startAmmo === undefined || !Number.isInteger(startAmmo) || startAmmo <= 0) {
    issues.push(`${label}의 시작 탄환이 양의 정수가 아니다`)
    return
  }
  const required = minAmmoWithoutMagazines(level)
  if (startAmmo < required) {
    issues.push(
      `${label}은 탄창 없이 깰 수 없다: 시작 탄환 ${String(startAmmo)}발로는 ` +
        `${String(minFound)}개를 찾기까지의 오답을 감당하지 못한다 (필요 ${String(required)}발)`,
    )
  }
}

/**
 * 단계 데이터 전량 검증. 문제를 **모아서** 돌려준다(`floorPlan.validateFloorPlan`과 같은 규약).
 * 단계를 끼워 넣거나 수치를 만질 때 곡선이 조용히 깨지는 것을 테스트가 여기서 잡는다.
 */
export function validateLevels(levels: readonly LevelDef[] = LEVELS): readonly string[] {
  const issues: string[] = []

  if (levels.length === 0) {
    issues.push('단계가 하나도 없다')
    return issues
  }

  const seenSeeds = new Set<string>()
  levels.forEach((level, index) => {
    validateLevel(level, index, issues)
    if (seenSeeds.has(level.seed)) issues.push(`시드 '${level.seed}'가 겹친다 - 두 단계가 같은 방이 된다`)
    seenSeeds.add(level.seed)
  })

  validateCurve(levels, issues)
  validateVerbs(levels, issues)

  return issues
}
